import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories as categoryList } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const comic = useMemo(() => comics.find((c) => c.slug === slug), [slug]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter(
        (c) =>
          c.id !== comic.id &&
          c.categories.some((cat) => comic.categories.includes(cat))
      )
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categoryList.find((c) => c.slug === comic.categories[0])?.name || '';
  const statusText = comic.status === 'ongoing' ? '连载中' : '已完结';
  const statusClass = comic.status === 'ongoing'
    ? 'bg-accent-100 text-accent-800'
    : 'bg-secondary-100 text-secondary-800';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    image: comic.cover,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[0]?.updatedAt,
    dateModified: comic.updatedAt,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{
        name: `${comic.title} - 韩漫画免费阅读`,
        description: `${comic.title}，${comic.author}作品。${comic.description.slice(0, 120)}`,
        url: `https://www.hanmanhua.com/comic/${comic.slug}`,
        inLanguage: 'zh-CN',
      }} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${comic.categories[0]}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        {/* Comic Info */}
        <section className="w-full bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-48 md:w-56 mx-auto md:mx-0">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 好看的韩国漫画大全`}
                  className="w-full rounded-lg shadow-sm"
                />
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                    {comic.title}
                  </h1>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusClass}`}>
                    {statusText}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                  <span>作者：{comic.author}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    {comic.rating} 分
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span>{(comic.views / 10000).toFixed(0)} 万阅读</span>
                  <span className="text-foreground-300">|</span>
                  <span>共 {comic.chapters.length} 话</span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {comic.categories.map((catSlug) => {
                    const cat = categoryList.find((c) => c.slug === catSlug);
                    return cat ? (
                      <Link
                        key={catSlug}
                        to={`/category/${catSlug}`}
                        className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        {cat.name}
                      </Link>
                    ) : null;
                  })}
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="mt-5 text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>

                <div className="mt-6">
                  <Link
                    to={`/comic/${comic.slug}`}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-play-circle-line"></i> 开始阅读第一话
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="w-full py-8 md:py-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-5">
              章节列表
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[...comic.chapters].reverse().map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">
                      第 {chapter.number} 话
                    </span>
                    <span className="text-sm text-foreground-500 truncate">
                      {chapter.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                    <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-eye-line"></i> {(chapter.views / 1000).toFixed(1)}k
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="w-full py-8 md:py-10 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}